import { api } from '../lib/api';

export interface ProductComponent {
  id?: number;
  componentProductId: number;
  qty: number;
  componentSku?: string | null;
  componentName?: string | null;
  componentUomItemId?: number | null;
  componentUomLabel?: string | null;
}

export interface PreparationMeta {
  legacyPrepId?: number | null;
  withdrawalDays?: number | null;
  treatmentLengthDays?: number | null;
  instructionUrl?: string | null;
}

export interface ProductMetaJson {
  preparation?: PreparationMeta | null;
}

export interface ProductSalesPriceHistoryRow {
  id: number;
  priceType: string;
  priceExVat: string;
  changedAt: string;
  changedByUserId: number | null;
  note: string | null;
}

export interface ProductSalesPriceRow {
  id: number;
  priceListId: number;
  priceListName: string;
  customerId: number | null;
  customerName: string | null;
  unitPriceExVat: string;
  vatRateItemId: number | null;
  vatRateLabel: string | null;
  validFrom: string | null;
  validTo: string | null;
  minQty: string | null;
  notes: string | null;
  updatedAt: string;
}

export interface ProductTopCustomerRow {
  customerId: number | null;
  customerName: string;
  shippedQty: string;
  shipmentCount: number;
  lastShipDate: string | null;
}

export interface ProductStats {
  totalPurchasedQty: string;
  purchaseDocumentsCount: number;
  lastPurchaseDate: string | null;
  totalOrderedQty: string;
  totalShippedQty: string;
  orderCount: number;
  shipmentCount: number;
  lastOrderDate: string | null;
  lastShipmentDate: string | null;
  topCustomers: ProductTopCustomerRow[];
}

export interface Product {
  id: number;
  sku: string | null;
  ean: string | null;
  parentProductId: number | null;
  name: string;
  kind: string;
  categoryItemId: number | null;
  defaultUomItemId: number | null;
  netWeightG: number | null;
  canPurchase: number;
  canSell: number;
  salesPriceRetailIncVat: string | null;
  salesPriceDefaultExVat: string | null;
  syncWooCommerce: number;
  syncZettle: number;
  syncGs1: number;
  syncFortnox: number;
  defaultVatRateItemId: number | null;
  notes: string | null;
  metaJson?: ProductMetaJson | null;
  isActive: number;
  categoryLabel?: string | null;
  uomLabel?: string | null;
  vatLabel?: string | null;
  parentSku?: string | null;
  parentName?: string | null;
}

export interface ProductDetail extends Product {
  components: ProductComponent[];
  priceHistory: ProductSalesPriceHistoryRow[];
  salesPrices: ProductSalesPriceRow[];
  stats: ProductStats;
}

export interface ProductSalesPriceMeta {
  priceLists: Array<{ id: number; name: string; isDefault: number }>;
  customers: Array<{ id: number; name: string }>;
  vatRates: Array<{ id: number; label: string }>;
}

export interface ProductFormData {
  sku?: string;
  ean?: string;
  parentProductId?: number | null;
  name: string;
  kind: string;
  categoryItemId?: number | null;
  defaultUomItemId?: number | null;
  netWeightG?: number | null;
  canPurchase?: boolean;
  canSell?: boolean;
  salesPriceRetailIncVat?: number | null;
  salesPriceDefaultExVat?: number | null;
  syncWooCommerce?: boolean;
  syncZettle?: boolean;
  syncGs1?: boolean;
  syncFortnox?: boolean;
  defaultVatRateItemId?: number | null;
  notes?: string;
  metaJson?: ProductMetaJson | null;
  components?: Array<{
    componentProductId: number;
    qty: number;
  }>;
}

export interface ProductSalesPriceFormData {
  priceListId: number;
  customerId?: number | null;
  unitPriceExVat: number;
  vatRateItemId?: number | null;
  validFrom?: string | null;
  validTo?: string | null;
  minQty?: number | null;
  notes?: string;
}

export const productsApi = {
  list: () => api.get<{ products: Product[] }>('/products'),
  get: (id: number) => api.get<ProductDetail>(`/products/${id}`),
  create: (data: ProductFormData) => api.post<{ id: number; name: string; kind: string }>('/products', data),
  update: (id: number, data: Partial<ProductFormData>) => api.patch<{ ok: boolean }>(`/products/${id}`, data),
  delete: (id: number) => api.delete<{ ok: boolean }>(`/products/${id}`),
  salesPriceMeta: (id: number) => api.get<ProductSalesPriceMeta>(`/products/${id}/sales-prices/meta`),
  createSalesPrice: (id: number, data: ProductSalesPriceFormData) =>
    api.post<{ id: number }>(`/products/${id}/sales-prices`, data),
  updateSalesPrice: (id: number, rowId: number, data: ProductSalesPriceFormData) =>
    api.patch<{ ok: boolean }>(`/products/${id}/sales-prices/${rowId}`, data),
  deleteSalesPrice: (id: number, rowId: number) =>
    api.delete<{ ok: boolean }>(`/products/${id}/sales-prices/${rowId}`),
};
